import messages from 'components/MainNav/messages';
import { NavLink, Link, withRouter } from 'react-router-dom';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import SVGIcon from '../SVGIcon/SVGIcon';
import CloseIcon from './CloseIcon.svg';
import Email from './Email.svg';
import KamereoSideNav from './KamereoSideNav.svg';
import Phone from './Phone.svg';
import GlobalIcon from './NavIcons/GlobalIcon.svg';
import { LANGUAGES } from 'utils/constants';
import utilsMessages from 'utils/messages';

/* ─────────────────────────── Styled components ─────────────────────────── */

const SideNavContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 60%;
  max-width: 320px;
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
  z-index: 1000;
  transition: transform 0.3s ease-in-out;
  transform: translate3d(-100%, 0, 0);
  &.opened {
    transform: translate3d(0, 0, 0);
  }
`;

const Inner = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  svg {
    height: 18px !important;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eeeeee;
  margin: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  flex: 1;
`;

const UpperSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionLabel = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #4b5563;
  line-height: 18px;
  margin: 0;
`;

const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: 12px;
`;

const MenuLink = styled.a`
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  line-height: 22px;
  text-decoration: none;
  display: block;
  &:hover {
    color: #43a047;
  }
`;

const LangRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LangSwitcher = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LangOptions = styled.div`
  display: flex;
  align-items: center;
`;

const LangOption = styled.span<{ isActive?: boolean }>`
  font-size: 14px;
  font-weight: ${({ isActive }) => (isActive ? 700 : 400)};
  color: ${({ isActive }) => (isActive ? '#3c9041' : '#111827')};
  cursor: pointer;
  line-height: 22px;
  padding: 0 4px;
  &:hover {
    color: #3c9041;
  }
`;

const LangDivider = styled.span`
  width: 1px;
  height: 14px;
  background: #bfc3cb;
  display: inline-block;
`;

const SupportSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 12px;
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
`;

const ContactText = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #3c9041;
  line-height: 22px;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
`;

const CtaSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CtaLink = styled(Link)<{ variant?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  text-decoration: none;
  ${({ variant }) =>
    variant === 'primary'
      ? `
    background: #43a047;
    color: #ffffff;
    border: 1px solid #43a047;
  `
      : `
    background: #ffffff;
    color: #111827;
    border: 1px solid #d1d5db;
  `}
  &:hover {
    opacity: 0.9;
  }
`;

const Signout = styled.div`
  color: #e2483c;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
`;

/* ─────────────────────────── Component ─────────────────────────── */

const SideNavMobile: FC<any> = ({ opened, onCloseSideNav, onSetLang, onSignout }) => {
  const lang = window.localStorage.getItem('lang') || 'vi';
  const loggedIn = !!localStorage.getItem('token');

  const aboutLink = lang === 'vi' ? 'https://kamereo.vn/ve-kamereo' : 'https://kamereo.vn/about-kamereo';
  const blogLink = `https://kamereo.vn/blog/${lang}/${lang === 'vi' ? 'tin-tuc-kamereo/' : 'kamereo-news/'}`;

  return (
    <>
      {opened && <Overlay onClick={onCloseSideNav} />}
      <SideNavContainer className={opened ? 'opened' : ''}>
        <Inner>
          {/* Header */}
          <Header>
            <NavLink to="/" onClick={onCloseSideNav}>
              <SVGIcon src={KamereoSideNav} />
            </NavLink>
            <CloseButton onClick={onCloseSideNav} aria-label="Close menu">
              <SVGIcon src={CloseIcon} renderAsImg={false} />
            </CloseButton>
          </Header>

          <Divider />

          {/* Main content */}
          <Content>
            <UpperSections>
              {/* Về Kamereo section */}
              <div>
                <SectionLabel style={{ marginBottom: 12 }}>
                  <FormattedMessage {...messages.aboutKamereo} />
                </SectionLabel>
                <MenuList>
                  <MenuLink href={aboutLink} target="_blank" rel="noopener noreferrer">
                    <FormattedMessage {...messages.kamereoIntroduction} />
                  </MenuLink>
                  <MenuLink href="https://tuyendung.kamereo.vn" target="_blank" rel="noopener noreferrer">
                    <FormattedMessage {...messages.careers} />
                  </MenuLink>
                  <MenuLink href={blogLink} target="_blank" rel="noopener noreferrer">
                    <FormattedMessage {...messages.blog} />
                  </MenuLink>
                </MenuList>
              </div>

              <Divider />

              {/* Language setting */}
              <LangRow>
                <SectionLabel>
                  <FormattedMessage {...messages.languageSetting} />
                </SectionLabel>
                <LangSwitcher>
                  <SVGIcon src={GlobalIcon} />
                  <LangOptions>
                    <LangOption isActive={lang === 'vi'} onClick={() => onSetLang(LANGUAGES.Vi)}>
                      VN
                    </LangOption>
                    <LangDivider />
                    <LangOption isActive={lang === 'en'} onClick={() => onSetLang(LANGUAGES.En)}>
                      EN
                    </LangOption>
                  </LangOptions>
                </LangSwitcher>
              </LangRow>

              <Divider />

              {/* Hỗ trợ section */}
              <SupportSection>
                <SectionLabel>
                  <FormattedMessage {...messages.support} />
                </SectionLabel>
                <ContactList>
                  <ContactItem href="mailto:info@kamereo.vn">
                    <SVGIcon src={Email} imgAttr={{ loading: 'lazy', width: '14px', height: '14px' }} />
                    <ContactText>info@kamereo.vn</ContactText>
                  </ContactItem>
                  <ContactItem href="tel:0812463727">
                    <SVGIcon src={Phone} imgAttr={{ loading: 'lazy', width: '14px', height: '14px' }} />
                    <ContactText>0812 46 37 27</ContactText>
                  </ContactItem>
                </ContactList>
              </SupportSection>

              <Divider />
            </UpperSections>

            {/* CTA buttons */}
            <CtaSection>
              {loggedIn ? (
                <Signout
                  onClick={() => {
                    onSignout && onSignout();
                    onCloseSideNav();
                  }}
                >
                  <FormattedMessage {...messages.signOut} />
                </Signout>
              ) : (
                <>
                  <CtaLink to="/contact-us" variant="primary" onClick={onCloseSideNav}>
                    <FormattedMessage {...messages.signUp} />
                  </CtaLink>
                  <CtaLink to="/signin" variant="secondary" onClick={onCloseSideNav}>
                    <FormattedMessage {...utilsMessages.signin} />
                  </CtaLink>
                </>
              )}
            </CtaSection>
          </Content>
        </Inner>
      </SideNavContainer>
    </>
  );
};

export default withRouter(SideNavMobile);
